import './BrandKitDetail.css'

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { hexFromArgb } from '@material/material-color-utilities'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { MaterialIcon, MtwProvider } from '@makyoapp/makyo-tw-ui'
import { IColor, Loading, useTheme } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'
import { toasted } from '@/lib/toast'
import { BrandKitDetailProvider, useBrandKitDetailContext } from '@/context/BrandKitDetailContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { colorToSource, sourceToHex } from '@/shared/brand-kit'
import { ORG_BRAND_KIT_ROUTE, ORG_ROUTE } from '@/shared/constants'
import { WrapperProps } from '@/shared/routes'
import { BrandKitLogo } from '@/components/BrandKit/BrandKitLogo'
import { ColorTokensDemo } from '@/components/BrandKit/ColorTokensDemo'
import { ComponentDemo } from '@/components/BrandKit/ComponentDemo'
import { TypographyDemo } from '@/components/BrandKit/TypographyDemo'
import Layout from '@/components/Layout'

const TABS = [
  {
    id: 'colors',
    icon: 'palette',
    label: 'Colors',
  },
  {
    id: 'typography',
    icon: 'text_fields',
    label: 'Typography',
  },
  {
    id: 'logos',
    icon: 'image',
    label: 'Logos',
  },
  {
    id: 'components',
    icon: 'widgets',
    label: 'Components',
  },
]

function BrandKitDetail() {
  const { state } = useTheme()
  const { brand, updateBrandDetail, handleUpdateFont } = useBrandKitDetailContext()
  const [activeTab, setActiveTab] = useState('components')
  const [isLoading, setIsLoading] = useState(false)

  const { t } = useTranslation()

  const extendedColors = useMemo(() => {
    if (!brand || !brand.colors?.[0]) return []
    return brand.colors[0].customColors.map((c) => {
      return {
        label: c.name,
        argb: c.value,
        hex: sourceToHex(c.value),
      }
    })
  }, [brand])

  if (!brand) return null

  const sourceColor = hexFromArgb(brand.colors[0].value)

  const typography = brand.typography

  const handleUpdateSourceColor = async (newColor: string | IColor) => {
    if (!newColor) return

    setIsLoading(true)

    const newColorArgb = colorToSource(newColor)
    const newBrand = { ...brand }
    newBrand.colors[0].value = newColorArgb

    try {
      await handleUpdateFont(newBrand)
      updateBrandDetail(newBrand)
      toasted(t('Source color updated'))
    } catch (error) {
      console.error('Error updating source color:', error)
      toasted(t('Failed to update source color'), { type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateExtendedColor = async (newColor: string | IColor, index: number) => {
    if (!newColor) return

    setIsLoading(true)

    const argbColor = colorToSource(newColor)

    const idx = 0
    const newBrandKit = { ...brand }
    const extendedColors = newBrandKit.colors[idx].customColors

    if (!extendedColors[index]) {
      extendedColors[index] = {
        name: `Extended ${extendedColors.length}`,
        value: argbColor,
        blend: false,
      }
    } else {
      // validate if name is matched the index + 1
      const expectedName = `Extended ${index + 1}`
      if (extendedColors[index].name !== expectedName) {
        extendedColors[index].name = expectedName
      }

      extendedColors[index].value = argbColor
    }

    try {
      await handleUpdateFont(newBrandKit)
      updateBrandDetail(newBrandKit)
      toasted(t('Source color updated'))
    } catch (error) {
      console.error('Error updating extended color:', error)
      toasted(t('Failed to update extended color'), { type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='flex flex-col gap-8'>
      <MtwProvider
        ctxDocument={document}
        extendedColors={extendedColors}
        sourceColor={sourceColor}
        typography={typography}
        mode={state.mode}
      >
        <div className='flex flex-col gap-4'>
          <div className='flex gap-2 border-b bdr'>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-1.5 px-4 py-2 border-b-2 transition-colors hover:bg-zinc-300 dark:hover:bg-slate-700',
                  activeTab === tab.id ? 'border-primary-500 text-primary-500' : 'border-transparent text-light'
                )}
              >
                <MaterialIcon icon={tab.icon} size={16} />
                <span className='font-medium'>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className='mt-2 grid gap-8'>
            {activeTab === 'colors' && (
              <div className='mk-bg border bdr rounded p-4'>
                <ColorTokensDemo
                  source={brand.colors[0].value}
                  extendedColors={extendedColors}
                  mode={state.mode}
                  onUpdateSourceColor={handleUpdateSourceColor}
                  onUpdateExtendedColor={handleUpdateExtendedColor}
                  isLoading={isLoading}
                />
              </div>
            )}
            {activeTab === 'typography' && (
              <div className='mk-bg border bdr rounded p-4'>
                <TypographyDemo typography={brand.typography} />
              </div>
            )}
            {activeTab === 'logos' && (
              <div className='mk-bg border bdr rounded p-4'>
                <BrandKitLogo withPadding={false} />
              </div>
            )}
            {activeTab === 'components' && (
              <div className='mk-bg border bdr rounded p-4'>
                <ComponentDemo color={brand.colors[0].value} onChange={handleUpdateSourceColor} isLoading={isLoading} />
              </div>
            )}
          </div>
        </div>
      </MtwProvider>
    </div>
  )
}

function BrandKitDetailWrapper({ title }: WrapperProps) {
  const { currentOrg } = useCurrentOrgContext()
  const orgId = currentOrg?.id
  const brandPath = `${ORG_ROUTE}/${orgId}/${ORG_BRAND_KIT_ROUTE}`

  const { t } = useTranslation()
  const canEditBrandKit = useOrgPerm(OrgPermission.BrandKitEdit)
  const { brand, isLoading, updateBrandDetail, handleUpdateFont } = useBrandKitDetailContext()

  // inline rename state
  const [isEditingName, setIsEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [isSavingName, setIsSavingName] = useState(false)

  useEffect(() => {
    if (brand && !isEditingName) {
      setNameDraft(brand.name)
    }
  }, [brand, isEditingName])

  const handleStartEdit = () => {
    if (!canEditBrandKit || !brand) return
    setNameDraft(brand.name)
    setIsEditingName(true)
  }

  const cancelEdit = () => {
    setIsEditingName(false)
    if (brand) setNameDraft(brand.name)
  }

  const commitRename = async () => {
    if (!brand || isSavingName) return
    const nextName = nameDraft.trim()
    if (!nextName || nextName === brand.name) {
      setIsEditingName(false)
      return
    }
    setIsSavingName(true)
    try {
      const updated = { ...brand, name: nextName }
      await handleUpdateFont(updated)
      updateBrandDetail(updated)
      toasted(t('Brand name updated'))
    } catch (error) {
      toasted(t('Failed to rename brand'), { type: 'error' })
    } finally {
      setIsSavingName(false)
      setIsEditingName(false)
    }
  }

  return (
    <Layout
      title={
        <div className='flex-c'>
          <Link className='text-light font-normal' title={title} to={brandPath}>
            {title}
          </Link>
          {brand && (
            <>
              <div>/</div>
              <div className='txt'>
                {isEditingName ? (
                  <input
                    className='bg-transparent border-b border-transparent focus:border-primary-500 outline-none px-1'
                    value={nameDraft}
                    disabled={isSavingName}
                    autoFocus
                    onChange={(e) => setNameDraft(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        commitRename()
                      } else if (e.key === 'Escape') {
                        e.preventDefault()
                        cancelEdit()
                      }
                      if (e.key === 'Space' || e.key === ' ') {
                        e.stopPropagation()
                      }
                    }}
                  />
                ) : (
                  <span
                    role={canEditBrandKit ? 'button' : undefined}
                    title={canEditBrandKit ? t('Double click to rename') : undefined}
                    onDoubleClick={handleStartEdit}
                    className={cn(canEditBrandKit && 'cursor-text hover:opacity-80')}
                  >
                    {brand.name}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      }
    >
      {isLoading ? <Loading /> : <BrandKitDetail />}
    </Layout>
  )
}

/**
 * Brand Kit Detail Page
 */
export default function BrandKitDetailPage({ title }: WrapperProps) {
  return (
    <BrandKitDetailProvider>
      <BrandKitDetailWrapper title={title} />
    </BrandKitDetailProvider>
  )
}
